import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Lazy load sections for maximum initial performance
const Hero = lazy(() => import('./components/Sections/Hero')) as React.FC<{ onReady?: (imageLoaded: boolean) => void }>;
const About = lazy(() => import('./components/Sections/About'));
const Education = lazy(() => import('./components/Sections/Education'));
const Work = lazy(() => import('./components/Sections/Work'));
const Skills = lazy(() => import('./components/Sections/Skills'));
const Projects = lazy(() => import('./components/Sections/Projects'));
const Certifications = lazy(() => import('./components/Sections/Certifications'));
const Achievements = lazy(() => import('./components/Sections/Achievements'));
const Contact = lazy(() => import('./components/Sections/Contact'));

import Navbar from './components/Navbar';
import ThemeToggle from './components/ThemeToggle';
import { ThemeProvider } from './context/ThemeContext';
import Login from './pages/admin/Login';
import Dashboard from './pages/admin/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import ParticlesBackground from './components/ParticlesBackground';
import ParticleCursor from './components/ParticleCursor';
import SplineBackground from './components/SplineBackground';

const sectionIds = [
  'home',
  'about',
  'education',
  'work',
  'skills',
  'projects',
  'certifications',
  'achievements',
  'contact',
];

const SectionSeparator = () => (
  <div className="w-full flex justify-center items-center py-12 opacity-60 pointer-events-none">
    <div className="h-[2px] w-1/3 md:w-1/4 bg-gradient-to-r from-transparent via-blue-500/50 dark:via-blue-400/50 to-transparent"></div>
    <div className="mx-4 md:mx-6 w-2 h-2 md:w-3 md:h-3 rounded-full bg-blue-500 dark:bg-blue-400 shadow-[0_0_12px_rgba(59,130,246,0.8)] animate-pulse"></div>
    <div className="h-[2px] w-1/3 md:w-1/4 bg-gradient-to-r from-transparent via-blue-500/50 dark:via-blue-400/50 to-transparent"></div>
  </div>
);

const PortfolioHome = () => {
  const [activeTab, setActiveTab] = useState('home');
  const isManualScroll = useRef(false);
  const manualScrollTimer = useRef<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [loadingStates, setLoadingStates] = useState({
    spline: false,
    heroData: false,
    heroImage: false
  });

  const updateProgress = useCallback((newStates: Partial<typeof loadingStates>) => {
    setLoadingStates(prev => {
      const next = { ...prev, ...newStates };
      
      // Calculate weighted progress
      let currentProgress = 0;
      if (next.heroData) currentProgress += 20;
      if (next.heroImage) currentProgress += 20;
      if (next.spline) currentProgress += 60;
      
      setProgress(currentProgress);
      
      if (currentProgress >= 100) {
        // Short delay for a smoother transition
        setTimeout(() => setIsLoading(false), 800);
      }
      
      return next;
    });
  }, []);

  // Sync with Spline
  const handleSplineLoad = useCallback(() => updateProgress({ spline: true }), [updateProgress]);
  
  // Sync with Hero
  const handleHeroReady = useCallback((imageLoaded: boolean) => {
    updateProgress({ heroData: true, heroImage: imageLoaded });
  }, [updateProgress]);

  useEffect(() => {
    // Fallback timer just in case something fails to load
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 8000); 
    return () => clearTimeout(timer);
  }, []);

  const handleTabChange = useCallback((tab: string) => {
    // Clear any existing timer
    if (manualScrollTimer.current) window.clearTimeout(manualScrollTimer.current);
    
    // Lock scroll-sync observer
    isManualScroll.current = true;
    setActiveTab(tab);
    
    const element = document.getElementById(`${tab}-wrap`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }

    // Release lock after the smooth scroll usually finishes
    manualScrollTimer.current = window.setTimeout(() => {
      isManualScroll.current = false;
    }, 1000);
  }, []);

  // Set up IntersectionObserver to update activeTab while scrolling
  useEffect(() => {
    if (isLoading) return;

    const observerOptions = {
      root: document.getElementById('main-scroll-container'),
      rootMargin: '-30% 0px -60% 0px', // Triggers when the top of the element hits the top 30% of the screen
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      // Don't update activeTab if we are currently mid-manual-scroll
      if (isManualScroll.current) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Extract original tab ID from wrapper ID
          const tabId = entry.target.id.replace('-wrap', '');
          setActiveTab(tabId);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sectionIds.forEach((id) => {
      const element = document.getElementById(`${id}-wrap`);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [isLoading]);

  return (
    <div className="min-h-screen bg-black overflow-hidden relative text-neutral-300">
      <AnimatePresence>
        {isLoading && (
          <motion.div 
            key="loading-screen"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="fixed inset-0 flex flex-col items-center justify-center bg-black z-[99999] pointer-events-auto"
          >
            {/* Subtle background robot hint (optional, but keep it clean) */}
            <div className="relative z-10 flex flex-col items-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="w-24 h-24 mb-8 relative"
              >
                <div className="absolute inset-0 border-4 border-blue-500/20 rounded-full" />
                <motion.div 
                  className="absolute inset-0 border-4 border-t-blue-500 rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                />
                <div className="absolute inset-0 flex items-center justify-center font-mono text-xl font-bold text-blue-400">
                  {progress}%
                </div>
              </motion.div>

              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-center"
              >
                <h2 className="text-2xl font-bold text-white mb-2 tracking-tighter uppercase">Initializing Engine</h2>
                <div className="w-48 h-1 bg-white/10 rounded-full overflow-hidden mb-4">
                  <motion.div 
                    className="h-full bg-gradient-to-r from-blue-600 to-purple-600"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-gray-500 text-xs animate-pulse">Syncing 3D Neural Modules...</p>
              </motion.div>
            </div>

            {/* Hint during loading */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 md:hidden">
              <p className="text-[10px] text-gray-400 uppercase tracking-widest whitespace-nowrap opacity-50">
                Use Desktop For Best Experience
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <SplineBackground onLoad={handleSplineLoad} />
      <Navbar activeTab={activeTab} onTabChange={handleTabChange} />
      {/* ThemeToggle removed to force dark mode */}

      {/* Persistent Experience Hint for mobile */}
      <div className="md:hidden fixed bottom-20 left-1/2 -translate-x-1/2 z-[40] px-4 py-2 rounded-full bg-white/5 backdrop-blur-md border border-white/10 shadow-2xl pointer-events-none">
        <p className="text-[10px] text-white font-medium uppercase tracking-widest whitespace-nowrap">
          Use Desktop For Better Viewing Experience
        </p>
      </div>
      
      {/* Make the main container pointer-events-none but allow scroll so Spline gets mouse movement */}
      {/* Standard touch/mouse interaction restored to main container to fix scroll stuckness */}
      <main id="main-scroll-container" className="md:ml-20 pb-16 md:pb-0 h-screen relative bg-transparent overflow-y-auto overflow-x-hidden scroll-smooth scroll-pt-16 md:scroll-pt-0 no-scrollbar overscroll-behavior-none">
        {/* We need inner contents to be interactive */}
        <div className="w-full relative z-10 flex flex-col">
          {/* Reverting to standard pointer events for better browser performance */}
          {/* Hardware accelerated section wrappers for smoother scrolling */}
          <div id="home-wrap" className="w-full transform-gpu will-change-transform"><Suspense fallback={<div className="min-h-screen bg-black" />}><Hero onReady={handleHeroReady} /></Suspense></div>
          <SectionSeparator />
          <div id="about-wrap" className="w-full transform-gpu will-change-transform"><Suspense fallback={<div className="min-h-[60vh] bg-black/5 animate-pulse rounded-3xl mx-4 my-8" />}><About /></Suspense></div>
          <SectionSeparator />
          <div id="education-wrap" className="w-full transform-gpu will-change-transform"><Suspense fallback={<div className="min-h-[60vh] bg-black/5 animate-pulse rounded-3xl mx-4 my-8" />}><Education /></Suspense></div>
          <SectionSeparator />
          <div id="work-wrap" className="w-full transform-gpu will-change-transform"><Suspense fallback={<div className="min-h-[60vh] bg-black/5 animate-pulse rounded-3xl mx-4 my-8" />}><Work /></Suspense></div>
          <SectionSeparator />
          <div id="skills-wrap" className="w-full transform-gpu will-change-transform"><Suspense fallback={<div className="min-h-[60vh] bg-black/5 animate-pulse rounded-3xl mx-4 my-8" />}><Skills /></Suspense></div>
          <SectionSeparator />
          <div id="projects-wrap" className="w-full transform-gpu will-change-transform"><Suspense fallback={<div className="min-h-[60vh] bg-black/5 animate-pulse rounded-3xl mx-4 my-8" />}><Projects /></Suspense></div>
          <SectionSeparator />
          <div id="certifications-wrap" className="w-full transform-gpu will-change-transform"><Suspense fallback={<div className="min-h-[60vh] bg-black/5 animate-pulse rounded-3xl mx-4 my-8" />}><Certifications /></Suspense></div>
          <SectionSeparator />
          <div id="achievements-wrap" className="w-full transform-gpu will-change-transform"><Suspense fallback={<div className="min-h-[60vh] bg-black/5 animate-pulse rounded-3xl mx-4 my-8" />}><Achievements /></Suspense></div>
          <SectionSeparator />
          <div id="contact-wrap" className="w-full transform-gpu"><Suspense fallback={<div className="min-h-[40vh] bg-black/5 animate-pulse rounded-3xl mx-4 my-8" />}><Contact /></Suspense></div>
        </div>
      </main>
    </div>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <Router>
        <ParticlesBackground />
        <ParticleCursor />
        <Routes>
          {/* Public Portfolio Route */}
          <Route path="/" element={<PortfolioHome />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<Login />} />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Redirect /admin to /admin/dashboard */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          
          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;